export class ScrollTracker {
  constructor(options = {}) {
    // Configuration
    this.options = {
      velocityMultiplier: options.velocityMultiplier || 15,
      maxVelocity: options.maxVelocity || 3,
      ...options,
    }

    // State variables
    this.lastScrollPosition = window.scrollY
    this.lastScrollTime = performance.now()
    this.scrollVelocity = 0
    this.scrollDirection = 'none'
    this.rafId = null
    this.listeners = new Map() // Using Map to store listener references with their IDs
    this.isTracking = false
    this.nextListenerId = 1

    // Bind methods
    this.updateScrollMetrics = this.updateScrollMetrics.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)

    // Initialize visibility change listener
    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    // Auto-cleanup on page unload
    window.addEventListener('unload', this.destroy.bind(this))

    // Start tracking by default
    this.start()
  }

  // Start tracking scrolls
  start() {
    if (!this.isTracking) {
      this.isTracking = true
      this.lastScrollPosition = window.scrollY
      this.lastScrollTime = performance.now()
      this.updateScrollMetrics()
    }
    return this
  }

  // Stop tracking
  stop() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    this.isTracking = false
    return this
  }

  // Clean up all resources
  destroy() {
    this.stop()
    this.listeners.clear()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('unload', this.destroy)
    return this
  }

  // Register a callback for scroll events and return a cleanup function
  onScroll(callback) {
    if (typeof callback !== 'function') return () => {}

    const id = this.nextListenerId++
    this.listeners.set(id, callback)

    if (!this.isTracking) {
      this.start()
    }

    // Return a cleanup function that the caller can use
    return () => {
      this.listeners.delete(id)
    }
  }

  // Remove a specific callback by ID
  offScroll(id) {
    this.listeners.delete(id)
    return this
  }

  // Handle visibility changes
  handleVisibilityChange() {
    if (document.hidden) {
      this.stop()
    } else if (this.listeners.size > 0) {
      this.start()
    }
  }

  // Calculate scroll metrics
  updateScrollMetrics() {
    const currentTime = performance.now()
    const currentPosition = window.scrollY
    const timeDelta = currentTime - this.lastScrollTime

    if (timeDelta > 0) {
      // Calculate distance moved since last check
      const distanceDelta = currentPosition - this.lastScrollPosition

      // Calculate velocity (pixels per millisecond)
      this.scrollVelocity = distanceDelta / timeDelta

      // Determine scroll direction
      this.scrollDirection = distanceDelta > 0 ? 'down' : distanceDelta < 0 ? 'up' : 'none'

      // Apply velocity dampening
      const velocityFactor = this.options.velocityMultiplier
      const maxVelocity = this.options.maxVelocity
      const dampedVelocity = Math.max(Math.min(this.scrollVelocity * velocityFactor, maxVelocity), -maxVelocity)

      // Notify all listeners
      if (this.listeners.size > 0) {
        const scrollData = {
          velocity: this.scrollVelocity,
          dampedVelocity: dampedVelocity,
          direction: this.scrollDirection,
          position: currentPosition,
          delta: distanceDelta,
          time: currentTime,
          timeDelta: timeDelta,
        }

        this.listeners.forEach((listener) => {
          try {
            listener(scrollData)
          } catch (error) {
            console.error('Error in scroll listener:', error)
          }
        })
      }

      // Update reference values
      this.lastScrollPosition = currentPosition
      this.lastScrollTime = currentTime
    }

    // Continue the animation loop if we still have listeners or are still tracking
    if (this.isTracking) {
      this.rafId = requestAnimationFrame(this.updateScrollMetrics)
    }
  }
}

// Default singleton instance for convenience
const scrollTracker = new ScrollTracker()
export default scrollTracker
